import { mapValues } from 'es-toolkit';
import { isPlainObject } from 'es-toolkit/compat';
import {
  core,
  safeParse,
  ZodArray,
  ZodCatch,
  ZodDefault,
  ZodError,
  ZodNonOptional,
  ZodNullable,
  ZodObject,
  ZodOptional,
  ZodPrefault,
  ZodReadonly,
  ZodType,
} from 'zod';

export interface FormikSchemaAdapter<TValues> {
  cast: (value: unknown) => TValues;
  validate: (value: unknown) => Promise<TValues>;
  validateSync: (value: unknown) => TValues;
}

export const FORM_ERROR_KEY = '_form';

export function zodToFormikSchema<TValues>(
  schema: ZodType<TValues>,
): FormikSchemaAdapter<TValues> {
  return {
    cast: (value) => castZodValue(schema, value) as TValues,
    validate: async (value) => {
      const result = await schema.safeParseAsync(value);

      if (!result.success) {
        throw toFormikValidationError(result.error, value);
      }

      return result.data;
    },
    validateSync: (value) => {
      const result = schema.safeParse(value);

      if (!result.success) {
        throw toFormikValidationError(result.error, value);
      }

      return result.data;
    },
  };
}

type ZodWrapperType =
  | ZodCatch
  | ZodDefault
  | ZodNonOptional
  | ZodNullable
  | ZodOptional
  | ZodPrefault
  | ZodReadonly;

function castZodValue(schema: core.$ZodType, value: unknown): unknown {
  const result = safeParseSync(schema, value);

  if (result?.success) {
    return result.data;
  }

  const unwrapped = unwrapZodSchema(schema);

  if (unwrapped instanceof ZodObject) {
    const source = isPlainObject(value)
      ? (value as Record<string, unknown>)
      : {};

    const cast: Record<string, unknown> = mapValues(
      unwrapped.shape,
      (fieldSchema, key) => castZodValue(fieldSchema, source[key]),
    );

    // `catchall` keys are not part of `shape`, so rebuilding from `shape` alone
    // would silently drop them — e.g. every dashboard filter param.
    const { catchall } = unwrapped.def;

    if (catchall) {
      for (const key of Object.keys(source)) {
        if (!(key in unwrapped.shape)) {
          cast[key] = castZodValue(catchall, source[key]);
        }
      }
    }

    return cast;
  }

  if (unwrapped instanceof ZodArray && Array.isArray(value)) {
    const { element } = unwrapped;

    return value.map((item) => castZodValue(element, item));
  }

  if (!result && value !== undefined) {
    return value;
  }

  return getZodDefault(schema);
}

function getZodDefault(schema: core.$ZodType): unknown {
  const result = safeParseSync(schema, undefined);

  if (result?.success) {
    return result.data;
  }

  if (schema instanceof ZodPrefault || schema instanceof ZodDefault) {
    return schema.def.defaultValue;
  }

  return isZodWrapperType(schema) ? getZodDefault(schema.unwrap()) : undefined;
}

function safeParseSync(
  schema: core.$ZodType,
  value: unknown,
): undefined | ReturnType<typeof safeParse> {
  try {
    return safeParse(schema, value);
  } catch (error: unknown) {
    if (error instanceof core.$ZodAsyncError) {
      return undefined;
    }

    throw error;
  }
}

function unwrapZodSchema(schema: core.$ZodType): core.$ZodType {
  return isZodWrapperType(schema) ? unwrapZodSchema(schema.unwrap()) : schema;
}

function isZodWrapperType(schema: core.$ZodType): schema is ZodWrapperType {
  return (
    schema instanceof ZodCatch ||
    schema instanceof ZodDefault ||
    schema instanceof ZodNonOptional ||
    schema instanceof ZodNullable ||
    schema instanceof ZodOptional ||
    schema instanceof ZodPrefault ||
    schema instanceof ZodReadonly
  );
}

interface FormikValidationError extends Error {
  name: 'ValidationError';
  path: string;
  value: unknown;
  errors: string[];
  inner: FormikValidationError[];
}

function toFormikValidationError(
  error: ZodError,
  value: unknown,
): FormikValidationError {
  const inner = error.issues.map((issue) =>
    createValidationError(issue.message, formatIssuePath(issue.path), value),
  );
  const rootError = createValidationError(
    inner[0]?.message ?? 'Invalid value',
    FORM_ERROR_KEY,
    value,
  );

  rootError.errors = inner.map(({ message }) => message);
  rootError.inner = inner;

  return rootError;
}

function createValidationError(
  message: string,
  path: string,
  value: unknown,
): FormikValidationError {
  const error = new Error(message) as FormikValidationError;

  error.name = 'ValidationError';
  error.path = path;
  error.value = value;
  error.errors = [message];
  error.inner = [];

  return error;
}

function formatIssuePath(path: readonly PropertyKey[]): string {
  if (path.length === 0) {
    return FORM_ERROR_KEY;
  }

  return path.reduce<string>((result, key) => {
    if (typeof key === 'number') {
      return `${result}[${key}]`;
    }

    return result ? `${result}.${String(key)}` : String(key);
  }, '');
}
